import Alert from "@mui/material/Alert";
import { useDialogs } from "../ui/DialogProvider";
import useEditorForm from "../hooks/useEditorForm";
import TextInput from "../ui/TextInput";
import Button from "@mui/material/Button";
import React from "react";
import Link from "../ui/RouterLink";
import { API_BASE, fetchJson } from "../utils";
import SetOptionsMenu from "../components/SetOptionsMenu";
import useSetCollection from "../hooks/useSetCollection";
import { nextSetName } from "../utils/setTracks";
export default function SetList() {
  const dialogs = useDialogs();
  const { sets, setSets, loading, error, reload } = useSetCollection();
  const [mutationError, setMutationError] = React.useState(null);
  const [pending, setPending] = React.useState(false);
  const busy = React.useRef(false);
  const [editing, setEditing, editorForm] = useEditorForm({
    id: null,
    name: "",
    original: "",
  });
  const createSet = async () => {
    if (busy.current || loading) return;
    busy.current = true;
    setPending(true);
    setMutationError(null);
    const name = nextSetName(sets);
    try {
      const set = await fetchJson(`${API_BASE}/user-sets/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          name,
        }),
      });
      setSets((s) => [...s, set]);
      setEditing({
        id: set.id,
        name: set.name,
        original: set.name,
      });
    } catch (err) {
      setMutationError(err);
    } finally {
      busy.current = false;
      setPending(false);
    }
  };
  const renameSet = async (id, name) => {
    if (busy.current) return;
    busy.current = true;
    setPending(true);
    setMutationError(null);
    try {
      await fetchJson(`${API_BASE}/user-sets/${id}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          name,
        }),
      });
      setSets((s) =>
        s.map((set) =>
          set.id === id
            ? {
                ...set,
                name,
              }
            : set,
        ),
      );
      setEditing({
        id: null,
        name: "",
        original: "",
      });
    } catch (err) {
      setMutationError(err);
    } finally {
      busy.current = false;
      setPending(false);
    }
  };
  const startRename = (id, current) => {
    if (busy.current) return;
    setEditing({
      id,
      name: current,
      original: current,
    });
  };
  const finishRename = () => {
    if (busy.current) return;
    const { id, name, original } = editing;
    const trimmed = name.trim();
    if (!id || trimmed === original) {
      setEditing({
        id: null,
        name: "",
        original: "",
      });
      return;
    }
    if (!trimmed) {
      setMutationError(new Error("Set name cannot be empty."));
      return;
    }
    renameSet(id, trimmed);
  };
  const deleteSet = async (id) => {
    if (busy.current || !(await dialogs.confirm("Delete this set?"))) return;
    if (busy.current) return;
    busy.current = true;
    setPending(true);
    setMutationError(null);
    try {
      await fetchJson(`${API_BASE}/user-sets/${id}/`, {
        method: "DELETE",
        credentials: "include",
      });
      setSets((s) => s.filter((set) => set.id !== id));
    } catch (err) {
      setMutationError(err);
    } finally {
      busy.current = false;
      setPending(false);
    }
  };
  if (error) {
    return (
      <Alert role="alert" severity="error">
        Error loading sets: {error.message}{" "}
        <Button className="btn" onClick={reload}>
          Retry
        </Button>
      </Alert>
    );
  }
  return (
    <div>
      {loading && <p role="status">Loading sets…</p>}
      {mutationError && (
        <Alert role="alert" severity="error">
          {mutationError.message}
        </Alert>
      )}
      <ul className="album-grid">
        <li className="album-tile">
          <Button
            className="new-set-button"
            aria-label="New Set"
            onClick={createSet}
            disabled={pending || loading}
            variant="text"
            sx={{ aspectRatio: "1", width: "100%", fontSize: "3rem" }}
          >
            +
          </Button>
          <div className="album-info">
            <div className="album-title">New Set</div>
          </div>
        </li>
        {sets.map((set) => (
          <li key={set.id} className="album-tile">
            <Link
              className="album-thumb"
              to={`/sets/${set.id}`}
              aria-label={`Open ${set.name}`}
            >
              <img
                src={`${API_BASE}/user-sets/${set.id}/image/`}
                alt={`${set.name} artwork`}
                loading="lazy"
              />
            </Link>
            <div className="album-info">
              {editing.id === set.id ? (
                <TextInput
                  className="set-edit-input"
                  type="text"
                  aria-label="Set name"
                  disabled={pending}
                  value={editing.name}
                  onChange={(e) =>
                    setEditing((ed) => ({
                      ...ed,
                      name: e.target.value,
                    }))
                  }
                  onBlur={editorForm.handleSubmit(finishRename)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.target.blur();
                    if (e.key === "Escape")
                      setEditing({
                        id: null,
                        name: "",
                        original: "",
                      });
                  }}
                  autoFocus
                />
              ) : (
                <div className="album-title">{set.name}</div>
              )}
              <div>
                {set.track_count ?? 0} tracks
                {set.total_duration ? ` • ${set.total_duration}` : ""}
              </div>
              {set.missing_duration_count > 0 && (
                <div className="set-subtitle">
                  ({set.missing_duration_count} missing durations)
                </div>
              )}
              <SetOptionsMenu
                onRename={() => startRename(set.id, set.name)}
                onDelete={() => deleteSet(set.id)}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
